<script lang="ts">
    import AlternativesKanbanPage from '@Alternatives/AlternativesLists/Pages/AlternativesKanbanPage'
    export default AlternativesKanbanPage
</script>

<template>
    <div>
        <AlternativeEditorDialog :viewModel="alternativeEditorDialog" />

        <AlternativesComparisonDialog :viewModel="alternativesComparisonDialog" />

        <ViewSettingsEditor :viewModel="viewSettingsEditor" @saved="onViewSettingsUpdated" />

        <ListSelectionDialog :viewModel="statusListSelectionDialog" @update-value="handleUpdateMultipleAlternativesStatusCommand" />
        <ListSelectionDialog :viewModel="attrListSelectionDialog" @update-value="handleMultipleAlternativesAttributeValueUpdateCommand" />
        <NumericInputDialog :viewModel="numericInputDialog" @update-value="handleMultipleAlternativesAttributeValueUpdateCommand" />
        <MonthPickerDialog :viewModel="startDateMonthPicker" @update-date="handleUpdateMultipleAlternativesStartDateCommand" />
        <MonthPickerDialog :viewModel="commitmentDateMonthPicker" @update-date="handleUpdateMultipleAlternativesCommitmentDateCommand" />

        <PortfolioNavigation :workspaceId="workspaceId" :projectId="projectId" />

        <div v-if="rm" class="container-fluid" style="margin-top: 80px; padding-top: 20px;">
            <div class="row">
                <div class="col-lg-6">
                    <div>
                        <h6 class="text-muted mt-2">{{rm.TchProjectName}}</h6>
                    </div>
                    <div class="d-flex align-items-center">
                        <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">{{rm.AlternativesLabel}}:</h1>
                        <div class="dropdown ml-2">
                            <a class="btn btn-link" style="font-size: 2em; padding: 0;" data-toggle="dropdown">
                                {{viewDefName}} <i class="fa fa-caret-down" style="font-size: 0.7em; vertical-align: middle;"></i>
                            </a>
                            <div class="dropdown-menu dropdown-menu-scrollable" role="menu">
                                <a class="dropdown-item" @click="setViewDef()">All</a>
                                <div class="dropdown-divider"></div>
                                <h6 class="dropdown-header">Custom lists</h6>
                                <a class="dropdown-item" v-for="v in rm.Views" :key="v.Id" @click="setViewDef(v.Id)">{{v.Name}}</a>
                            </div>
                        </div>
                    </div>

                </div>
                <div class="col-lg-6 text-right align-self-end">
                    <div class="btn-group">
                        <button type="button" class="btn btn-outline-secondary dropdown-toggle" style="padding: 0.7em 2em;" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                            Actions
                        </button>
                        <div class="dropdown-menu">
                            <a class="dropdown-item" v-sanitizedHref="`/#/import-alternatives/${workspaceId}/${projectId}/${rm.Id}`">Import</a>
                            <a class="dropdown-item" @click="handleExportAlternativesCommand">Export</a>
                            <div class="dropdown-divider"></div>
                            <a class="dropdown-item" v-sanitizedHref="`/#/kanban-statuses/${workspaceId}/${projectId}`">Edit statuses</a>
                            <a class="dropdown-item" v-sanitizedHref="`/#/alternatives-labels/${workspaceId}/${projectId}`">Edit labels</a>
                        </div>
                    </div>

                    <button role="button" class="btn btn-success" style="padding: 0.7em 2em;" @click="showAlternativeCreator">
                        Create <span style="text-transform: lowercase;">{{rm.AlternativeLabel}}</span>
                    </button>
                    <a class="btn btn-lg btn-outline-secondary" href="https://www.transparentchoice.com/user-guide/pipeline" title="User guide: Configure pipeline" target="_blank">
                        <i class="fa fa-info" aria-hidden="true"></i>
                    </a>
                </div>
            </div>
            <hr style="border-color: #bbb; margin: 20px 0 20px;" />
            <nav class="navbar navbar-light navbar-expand-md bg-light" role="navigation">

                <div class="btn-group mr-2" role="group">
                    <button class="btn btn-outline-secondary" type="button" @click="showViewSettingsEditor">
                        <i class="fa fa-cog" aria-hidden="true"></i>
                    </button>
                    <button class="btn btn-outline-secondary" type="button" @click="collapseAllColumns" title="Collapse all columns">
                        <i class="fa fa-chevron-left"></i>
                    </button>
                    <button class="btn btn-outline-secondary" type="button" @click="expandAllColumns" title="Expand all columns">
                        <i class="fa fa-chevron-right"></i>
                    </button>
                </div>

                <div class="btn-group mr-2" role="group">
                    <div class="btn-group">
                        <button class="btn btn-outline-secondary" type="button" data-toggle="dropdown" aria-expanded="false">
                            <i class="fa-regular fa-square-check"></i> ({{selectedAlternatives?.length}})
                        </button>
                        <div class="dropdown-menu dropdown-menu-scrollable">
                            <a class="dropdown-item" @click="selectAll()">Select all</a>
                            <a class="dropdown-item" @click="unselectAll()">Unselect all</a>
                            <a class="dropdown-item" @click="invertSelection()">Invert selection</a>
                        </div>
                    </div>

                    <div class="btn-group">
                        <button class="btn btn-outline-secondary" type="button" data-toggle="dropdown" aria-expanded="false" :disabled="!selectedAlternatives?.length">
                            <i class="fa fa-pencil" aria-hidden="true"></i> Edit
                        </button>
                        <div class="dropdown-menu  dropdown-menu-scrollable">
                            <h6 class="dropdown-header">Edit selected</h6>
                            <div v-if="selectedAlternatives?.length">
                                <a class="dropdown-item" @click="showBulkStatusEditor">Status</a>
                                <div v-for="(items, section) in bulkEditablelistAttributesBySection" :key="section">
                                    <h6 v-if="section" class="dropdown-header">{{section}}</h6>
                                    <a v-for="attr in items" :key="attr.Id" class="dropdown-item" @click="showBulkAttributeValueEditor(attr)">{{attr.Name}}</a>
                                </div>
                            </div>
                            <div class="dropdown-divider"></div>
                            <a class="dropdown-item" @click="handleDeleteAlternativesCommand">Delete</a>
                        </div>
                    </div>

                    <div class="btn-group">
                        <button class="btn btn-outline-secondary" type="button" data-toggle="dropdown" aria-expanded="false" :disabled="!selectedAlternatives?.length">
                            <i class="fa-regular fa-calendar" aria-hidden="true"></i> Dates
                        </button>
                        <div class="dropdown-menu  dropdown-menu-scrollable">
                            <h6 class="dropdown-header">Edit selected</h6>
                            <div v-if="selectedAlternatives?.length">
                                <a class="dropdown-item" @click="showBulkStartDateMonthPicker">Start date</a>
                                <a class="dropdown-item" @click="showBulkCommitmentDateMonthPicker">Commitment date</a>
                            </div>
                        </div>
                    </div>


                    <button type="button" class="btn btn-outline-secondary" @click="showSelectedAlternativesComparison" :disabled="!selectedAlternatives?.length">
                        <i class="fa fa-balance-scale" aria-hidden="true"></i> Compare
                    </button>

                </div>


                <form class="form-inline my-2 my-lg-0 ml-auto" role="search">
                    <input v-model="searchPhrase" class="form-control mr-sm-2" type="search" placeholder="Search" aria-label="Search">
                </form>

                <div class="btn-group">
                    <button class="btn btn-outline-secondary dropdown-toggle" type="button" data-toggle="dropdown" aria-expanded="false">
                        {{orderByLabel}}
                    </button>
                    <div class="dropdown-menu dropdown-menu-right dropdown-menu-scrollable" role="menu">
                        <h6 class="dropdown-header">Order by:</h6>
                        <a class="dropdown-item" @click="setOrderBy('name')">Name</a>
                        <a class="dropdown-item" @click="setOrderBy('modified')">Date modified</a>
                        <a class="dropdown-item" @click="setOrderBy('create date')">Date created</a>
                        <div class="dropdown-divider"></div>
                        <div v-for="(items, section) in attributesBySection" :key="section">
                            <h6 v-if="section" class="dropdown-header">{{section}}</h6>
                            <a v-for="attr in items" :key="attr.Id" class="dropdown-item" @click="setOrderBy(attr.Id.toString())">{{attr.Name}}</a>
                        </div>
                    </div>
                </div>
            </nav>
            <div class="mt-3" style="height: calc(100% - 60px);">
                <div class="row">
                    <div class="col-lg-12">

                        <div style="height: calc(100vh - 300px);">
                            <div @dragover="onDragOverContainer" style="overflow-x: auto; overflow-y: hidden; height: 100%; width: 100%; white-space: nowrap;">
                                <div v-if="columns" v-for="column in columns" style="display: inline-block; height: 100%;">

                                    <div v-if="column.IsCollapsed === true" class="card pointer mr-1" @click="column.IsCollapsed = false" style="width: 50px; height: 100%;">
                                        <div class="card-header">
                                            <i class="fa fa-angle-right"></i>
                                        </div>
                                        <div @dragover.prevent
                                             @dragenter="onDragEnter($event, column)"
                                             @dragleave="onDragLeave($event, column)"
                                             @drop="onDrop($event, column)"
                                             :class="{ 'drag-over': column.dragOver }" class="card-body bg-light"
                                             style="overflow-x: hidden; overflow-y: auto;">

                                            <h5 style="transform: rotate(90deg);">{{column.title}}</h5>
                                        </div>
                                    </div>
                                    <div v-if="column.IsCollapsed === false" class="card mr-1" style="width: 19vw; min-width: 300px; height: 100%;">
                                        <div class="card-header">
                                            <div class="dropdown float-right">
                                                <a data-toggle="dropdown" class="text-dark">
                                                    <i class="fa fa-ellipsis-h"></i>
                                                </a>
                                                <div class="dropdown-menu dropdown-menu-right" role="menu">
                                                    <a class="dropdown-item" @click="column.selectAll()">Select all</a>
                                                    <a class="dropdown-item" @click="column.invertSelection()">Invert selection</a>
                                                    <a class="dropdown-item" @click="column.unselectAll()">Unselect all</a>
                                                </div>
                                            </div>

                                            <div class="pointer" @click="column.IsCollapsed = true">
                                                <span>{{column.title}}</span>
                                            </div>
                                        </div>

                                        <div @dragover.prevent
                                             @dragenter="onDragEnter($event, column)"
                                             @dragleave="onDragLeave($event, column)"
                                             @drop="onDrop($event, column)"
                                             :class="{ 'drag-over': column.dragOver }" class="card-body"
                                             style="overflow-x: hidden; overflow-y: auto;">

                                            <div v-for="a in column.alternativesView"
                                                 :key="a.Id"
                                                 :draggable="true"
                                                 @dragstart="onDragStart($event, a)">
                                                <div class="tile1" :class="getColorCssClass(a.Id)">
                                                    <div class="float-right">
                                                        <a @click="showAlternativeEditor(a.Id)"><i class="fa fa-pencil"></i></a>
                                                    </div>

                                                    <p style="white-space: normal;">
                                                        <input v-model="a.IsSelected" type="checkbox">
                                                        <a v-sanitizedHref="`/#/alternative/dashboard/${workspaceId}/${projectId}/${a.Id}`">
                                                            {{a.Name}}
                                                        </a>
                                                    </p>
                                                    <h6 class="font-italic mb-0">
                                                        <small>{{ getColorLabel(a.Id) }}</small>
                                                    </h6>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<style scoped>
    .tile1 {
        padding: 2px 6px 8px 8px;
        margin-bottom: 10px;
        line-height: 1.42857143;
        /*    background-color: rgba(86, 86, 90, 0.1);*/
        border: 1px solid rgba(86, 86, 90, 0.3);
        border-radius: 4px;
    }

        .tile1:hover {
            border-color: rgba(86, 86, 90, 0.5);
        }

        .tile1 a {
            color: #000;
        }

        .tile1 a:hover {
            text-decoration: underline !important;
            cursor: pointer;
        }

    .drag-over {
        border: 2px dashed #333; 
        background-color: orange;
    }

    .custom-rounded-right {
        border-top-right-radius: 0.25rem !important; /* Adjust based on your theme */
        border-bottom-right-radius: 0.25rem !important;
    }
</style>